import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Quote } from 'lucide-react';
import BreathingRoomAttribution from '../components/BreathingRoomAttribution';

const BOOKING_URL = "https://www.wellnessliving.com/rs/appointment-new/breathing_room-rws2se?dtu_redirect=2026-01-23+21%3A42%3A29&id_class_tab=3&id_mode=1&s_id=IXbkTr";

const TestimonialsPage = () => {
  const testimonials = [
    {
      quote: "Working with Jill helped me reconnect with my body in ways I never thought possible. Her grounded approach was exactly what I needed after years of trying to 'fix' myself with the latest trends.",
      name: "Sarah M.",
      detail: "Health Coaching Client",
      category: "Health Coaching",
    },
    {
      quote: "After years of trying quick fixes and biohacks, Jill's back-to-basics philosophy finally helped me understand what my body was trying to tell me. The fascial work has been transformative.",
      name: "Michael R.",
      detail: "Movement & Coaching Client",
      category: "Movement",
    },
    {
      quote: "I came to Jill after a major health setback that left me feeling disconnected from myself. Through her IMESP work, I've rediscovered parts of myself I'd lost. The whole-person approach made all the difference.",
      name: "Jennifer L.",
      detail: "Holistic Coaching Client",
      category: "Holistic Coaching",
    },
    {
      quote: "The gut healing protocol Jill put me on changed everything. I had tried so many diets before, but her metabolic typing approach and Breaking the Vicious Cycle philosophy finally made sense for my body.",
      name: "David K.",
      detail: "Health Coaching Client",
      category: "Health Coaching",
    },
    {
      quote: "Jill doesn't promise quick fixes—she promises real work that leads to real change. The movement sessions have helped me reconnect with my body after years of chronic pain.",
      name: "Amanda T.",
      detail: "Restorative Movement Client",
      category: "Movement",
    },
    {
      quote: "I was skeptical about the 'whole self' approach, but working with Jill through the five realms opened my eyes. Sometimes physical issues have emotional and spiritual components we don't see.",
      name: "Robert H.",
      detail: "IMESP Program Participant",
      category: "Holistic Coaching",
    },
    {
      quote: "The group wellness sessions are a highlight of my week. Jill creates such a grounded, supportive space where you can really tune into what your body needs.",
      name: "Lisa M.",
      detail: "Group Session Participant",
      category: "Movement",
    },
    {
      quote: "After my identity crisis, I didn't know who I was anymore. Jill's coaching helped me rebuild from the ground up—literally and figuratively. I'm grateful for her patient, rooted approach.",
      name: "Chris P.",
      detail: "Life Coaching Client",
      category: "Holistic Coaching",
    },
  ];

  return (
    <div className="bg-cream pt-24" data-testid="testimonials-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
              Client Stories
            </p>
            <h1 className="font-heading text-5xl md:text-6xl text-forest leading-[1.1] mb-6">
              Transformations<br />
              <span className="italic text-terracotta">from the Ground Up</span>
            </h1>
            <p className="text-stone-600 text-lg font-body leading-relaxed">
              Real stories from real people who chose slow, grounded healing 
              over quick fixes. Their journeys inspire and remind us that 
              transformation takes time—and it's worth it.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: (index % 2) * 0.15 }}
                data-testid={`testimonial-${index + 1}`}
                className="bg-stone-100 p-8 md:p-10 rounded-2xl relative"
              >
                <Quote size={40} className="text-terracotta/20 absolute top-6 right-6" />
                <span className="inline-block bg-terracotta/10 text-terracotta px-3 py-1 rounded-full font-body text-xs tracking-wide uppercase mb-6">
                  {testimonial.category}
                </span>
                <p className="font-heading text-xl md:text-2xl text-forest leading-relaxed mb-8 italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-body font-semibold text-forest">
                    {testimonial.name}
                  </p>
                  <p className="text-stone-500 font-body text-sm">
                    {testimonial.detail}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-stone-100">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-4xl md:text-5xl text-forest mb-6">
              Your Story Is Next
            </h2>
            <p className="text-stone-600 text-lg font-body leading-relaxed mb-10 max-w-2xl mx-auto">
              Every transformation starts with a single step—usually the hardest one. 
              If you're ready to begin your journey back to yourself, I'm here.
            </p>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="testimonials-book-btn"
              className="bg-forest text-cream px-8 py-4 rounded-full font-body font-medium hover:bg-forest-dark transition-all duration-300 inline-flex items-center gap-2"
            >
              Start Your Journey
              <ArrowRight size={18} />
            </a>
            <div className="mt-6 flex justify-center">
              <BreathingRoomAttribution align="center" />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TestimonialsPage;
